import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import { eventService } from '../services/eventService'
import { agendaService } from '../services/agendaService'
import { track } from '../services/telemetry'
import type { Session, Speaker } from '../data/types'
import { formatDayLabel, groupBy } from '../utils/format'
import PageContainer from '../components/ui/PageContainer'
import { EmptyState, LoadingState } from '../components/ui/States'
import SessionCard from '../components/SessionCard'
import { useAppData } from '../contexts/AppDataContext'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'

function dayKey(iso: string) {
  return new Date(iso).toISOString().slice(0, 10)
}

export default function AgendaPage() {
  const { savedSessionIds } = useAppData()
  const { isAuthenticated } = useAttendeeAuth()
  const [sessions, setSessions] = useState<Session[]>([])
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [loading, setLoading] = useState(true)
  const [dayIndex, setDayIndex] = useState(0)
  const [filter, setFilter] = useState<'all' | 'mine'>('all')

  useEffect(() => {
    track('agenda_view')
    const load = () =>
      Promise.all([eventService.getSessions(), eventService.getSpeakers()]).then(
        ([s, sp]) => {
          setSessions(s)
          setSpeakers(sp)
          setLoading(false)
        },
      )
    load()
    // Live-refresh when the agenda changes (SSE broadcast).
    const onChanged = (e: Event) => {
      const kind = (e as CustomEvent).detail?.kind
      if (kind === 'agenda' || kind === 'event') load()
    }
    window.addEventListener('vc:changed', onChanged)
    return () => window.removeEventListener('vc:changed', onChanged)
  }, [])

  const days = useMemo(() => {
    const keys = [...new Set(sessions.map((s) => dayKey(s.startsAt)))].sort()
    return keys
  }, [sessions])

  const visible = useMemo(() => {
    const currentDay = days[dayIndex]
    return sessions
      .filter((s) => dayKey(s.startsAt) === currentDay)
      .filter((s) => (filter === 'mine' ? savedSessionIds.has(s.id) : true))
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
  }, [sessions, days, dayIndex, filter, savedSessionIds])

  const exportCalendar = async () => {
    try {
      const url = await agendaService.icsUrl()
      window.open(url, '_blank')
    } catch {
      /* no-op */
    }
  }

  if (loading) return <LoadingState />

  const showExport = filter === 'mine' && isAuthenticated && savedSessionIds.size > 0

  return (
    <PageContainer
      title="Agenda"
      subtitle="Your personalized schedule for the Forum"
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {showExport && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalendarMonthRoundedIcon />}
              onClick={exportCalendar}
            >
              Add to calendar
            </Button>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_, v) => v && setFilter(v)}
          >
            <ToggleButton value="all" sx={{ px: 2 }}>
              All
            </ToggleButton>
            <ToggleButton value="mine" sx={{ px: 2 }}>
              My schedule
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      }
    >
      <Tabs
        value={dayIndex}
        onChange={(_, v) => setDayIndex(v)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {days.map((d, i) => (
          <Tab
            key={d}
            label={`Day ${i + 1}`}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          />
        ))}
      </Tabs>

      {days[dayIndex] && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {formatDayLabel(days[dayIndex])}
          </Typography>
          <Chip
            size="small"
            label={`${visible.length} ${visible.length === 1 ? 'item' : 'items'}`}
            sx={{ bgcolor: 'rgba(0,117,188,0.1)', color: 'primary.main', fontWeight: 600 }}
          />
        </Stack>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon={<EventBusyRoundedIcon fontSize="large" />}
          title={filter === 'mine' ? 'Nothing saved for this day' : 'No sessions'}
          description={
            filter === 'mine'
              ? 'Tap the bookmark on any session to build your personal schedule.'
              : 'Check back soon — the agenda is being finalized.'
          }
        />
      ) : (
        <SessionTimeline sessions={visible} speakers={speakers} />
      )}
    </PageContainer>
  )
}

function SessionTimeline({
  sessions,
  speakers,
}: {
  sessions: Session[]
  speakers: Speaker[]
}) {
  // Group by morning/afternoon/evening for a lightweight visual rhythm.
  const buckets = groupBy(sessions, (s) => {
    const h = new Date(s.startsAt).getHours()
    if (h < 12) return 'Morning'
    if (h < 17) return 'Afternoon'
    return 'Evening'
  })

  return (
    <Stack spacing={3}>
      {[...buckets.entries()].map(([label, items]) => (
        <Box key={label}>
          <Divider textAlign="left" sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              {label}
            </Typography>
          </Divider>
          <Stack spacing={1.5}>
            {items.map((s) => (
              <SessionCard key={s.id} session={s} speakers={speakers} />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}
